import React from 'react';
import Select from 'react-select';
import MemoIconCheckmarkCircle from '../icons/IconCheckmarkCircle';
import { isLenderPlan, isRealEstatePlan } from '../services/planService';
import { isValidNMLS } from '../validation';
import { getSelectStyles } from '../constants';

/**
 * ContactInfoSection component
 * Handles name, email, and professional license inputs
 */
const ContactInfoSection = ({
  // Form values
  firstName,
  lastName,
  email,
  nmlsId,
  stateLicense,
  selectedOffice,
  plan,
  companyId,
  // Touched state
  touchedFields,
  setFieldTouched,
  // Validation
  emailError,
  errors,
  validateEmail,
  // Handlers
  handleFieldChange,
  trackFieldInteraction,
  getFormContext,
  // Company data
  isLoadingCompanyData,
  companyDataError,
  officeProfiles,
  corporateProfile
}) => {
  const isFirstNameValid = firstName?.trim()?.length > 0;
  const isLastNameValid = lastName?.trim()?.length > 0;
  const isEmailValid = !emailError;
  const isNmlsIdValid = isValidNMLS(nmlsId?.trim() ?? '');
  const isStateLicenseValid = stateLicense?.trim()?.length > 0;

  const showNmlsField = isLenderPlan(plan);
  const showStateLicenseField = isRealEstatePlan(plan);

  return (
    <fieldset className="form-section-single-fieldset">
      <div className="form-section-single-legend-container">
        <legend className="form-section-single-legend">Your contact info</legend>
      </div>
      <div className="form-grid">
        <div className="form-group flex-row">
          {/* First Name */}
          <div className="form-group w-100">
            <label htmlFor="first-name">First name</label>
            <div className="form-group-input-container flex">
              <input
                type="text"
                id="first-name"
                value={firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                onFocus={() => trackFieldInteraction('firstName', 'focus', null, getFormContext())}
                onBlur={() => {
                  setFieldTouched('firstName');
                  trackFieldInteraction('firstName', 'blur', firstName, getFormContext());
                }}
                autoComplete="given-name"
                className="font-mono w-100"
              />
              {touchedFields.firstName && isFirstNameValid && !errors.firstName && (
                <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem', transform: 'translateY(-50%)' }}>
                  <MemoIconCheckmarkCircle />
                </span>
              )}
            </div>
            <div>
              {touchedFields.firstName && !isFirstNameValid && (
                <span className="error">First name is required</span>
              )}
              {!touchedFields.firstName && errors.firstName && <span className="error">{errors.firstName}</span>}
            </div>
          </div>

          {/* Last Name */}
          <div className="form-group w-100 mt-0" style={{ position: 'relative' }}>
            <label htmlFor="last-name">Last name</label>
            <div className="form-group-input-container flex">
              <input
                type="text"
                id="last-name"
                value={lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                onFocus={() => trackFieldInteraction('lastName', 'focus', null, getFormContext())}
                onBlur={() => {
                  setFieldTouched('lastName');
                  trackFieldInteraction('lastName', 'blur', lastName, getFormContext());
                }}
                autoComplete="family-name"
                className="font-mono w-100"
              />
              {touchedFields.lastName && isLastNameValid && !errors.lastName && (
                <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem', transform: 'translateY(-50%)' }}>
                  <MemoIconCheckmarkCircle />
                </span>
              )}
            </div>
            <div>
              {touchedFields.lastName && !isLastNameValid && (
                <span className="error">Last name is required</span>
              )}
              {!touchedFields.lastName && errors.lastName && <span className="error">{errors.lastName}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="email">Email</label>
        <div className="form-group-input-container flex">
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onFocus={() => trackFieldInteraction('email', 'focus', null, getFormContext())}
            onBlur={() => {
              setFieldTouched('email');
              validateEmail(email);
              trackFieldInteraction('email', 'blur', email, getFormContext());
            }}
            autoComplete="email"
            className="font-mono w-100"
          />
          {touchedFields.email && isEmailValid && !errors.email && (
            <span style={{ position: 'absolute', right: 10, top: '25%', color: '#4caf50', fontSize: '1.5rem' }}>
              <MemoIconCheckmarkCircle />
            </span>
          )}
        </div>
        <div>
          {(touchedFields.email && emailError) && <span className="error">{emailError}</span>}
          {errors.email && <span className="error">{errors.email}</span>}
        </div>
      </div>

      {/* NMLS ID for lenders */}
      {showNmlsField && (
        <div className="form-group full-width extra-margin-top w-50" style={{ position: 'relative' }}>
          <label htmlFor="nmls-id">NMLS ID</label>
          <div className="form-group-input-container flex">
            <input
              type="text"
              id="nmls-id"
              value={nmlsId}
              onChange={(e) => handleFieldChange('nmlsId', e.target.value)}
              onBlur={() => setFieldTouched('nmlsId')}
              autoComplete="off"
              className="font-mono w-100"
            />
          </div>
          {touchedFields.nmlsId && isNmlsIdValid && !errors.nmlsId && (
            <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem' }}>
              <MemoIconCheckmarkCircle />
            </span>
          )}
          {touchedFields.nmlsId && nmlsId?.trim() && !isNmlsIdValid && !errors.nmlsId && (
            <span className="error">NMLS ID must be 1–7 digits with no special characters</span>
          )}
          {errors.nmlsId && <span className="error">{errors.nmlsId}</span>}
        </div>
      )}

      {/* State License for real estate agents */}
      {showStateLicenseField && (
        <div className="form-group full-width extra-margin-top w-50" style={{ position: 'relative' }}>
          <label htmlFor="state-license">State license ID</label>
          <div className="form-group-input-container flex">
            <input
              type="text"
              id="state-license"
              value={stateLicense}
              onChange={(e) => handleFieldChange('stateLicense', e.target.value)}
              onBlur={() => setFieldTouched('stateLicense')}
              autoComplete="off"
              className="font-mono w-100"
            />
          </div>
          {touchedFields.stateLicense && isStateLicenseValid && !errors.stateLicense && (
            <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem' }}>
              <MemoIconCheckmarkCircle />
            </span>
          )}
          {errors.stateLicense && <span className="error">{errors.stateLicense}</span>}
        </div>
      )}

      {/* Office Selection for company signups */}
      {companyId && (
        <div className="form-group full-width extra-margin-top">
          <label htmlFor="office-select">Select Office</label>
          {isLoadingCompanyData && (
            <div style={{ padding: '1rem', color: '#aaa' }}>
              Loading office information...
            </div>
          )}
          {companyDataError && (
            <div style={{ padding: '1rem', color: '#ff6b6b' }}>
              Error loading offices: {companyDataError}
            </div>
          )}
          {!isLoadingCompanyData && !companyDataError && officeProfiles?.length > 0 && (
            <>
              <Select
                id="office-select"
                inputId="office-select"
                placeholder="Select an office…"
                menuPortalTarget={document.body}
                options={officeProfiles?.map(office => ({
                  value: office.id,
                  label: office.attributes?.name || office.attributes?.['office-name'] || `Office ${office.id}`
                })) || []}
                value={officeProfiles
                  ?.map(office => ({
                    value: office.id,
                    label: office.attributes?.name || office.attributes?.['office-name'] || `Office ${office.id}`
                  }))
                  ?.find(option => option.value === selectedOffice) || null}
                onChange={option => handleFieldChange('selectedOffice', option ? option.value : '')}
                isSearchable
                classNamePrefix="react-select"
                styles={getSelectStyles(68)}
              />
              {errors.selectedOffice && <span className="error">{errors.selectedOffice}</span>}
            </>
          )}
          {!isLoadingCompanyData && !companyDataError && officeProfiles?.length === 0 && corporateProfile && (
            <div style={{ padding: '1rem', color: '#aaa' }}>
              No offices available for this company.
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
};

export default ContactInfoSection;
